import { create } from 'zustand';
import type { Call } from '../services/api';
import type { CallSession } from '../services/sip';

export interface User {
  id: string;
  hubspot_user_id: string;
  email?: string;
  name?: string;
  phone_number?: string;
  sip_username?: string;
}

export interface AppState {
  user: User | null;
  isConnected: boolean;
  isRegistered: boolean;
  activeCalls: Map<string, Call>;
  sipSessions: Map<string, CallSession>;
  callHistory: Call[];
  selectedCallId: string | null;
}

export interface AppActions {
  setUser: (user: User | null) => void;
  setConnected: (connected: boolean) => void;
  setRegistered: (registered: boolean) => void;
  addCall: (call: Call) => void;
  updateCall: (callUUID: string, updates: Partial<Call>) => void;
  removeCall: (callUUID: string, finalCallData?: Call) => void;
  addSIPSession: (session: CallSession) => void;
  updateSIPSession: (sessionId: string, updates: Partial<CallSession>) => void;
  removeSIPSession: (sessionId: string) => void;
  setCallHistory: (history: Call[]) => void;
  addToCallHistory: (call: Call) => void;
  setSelectedCallId: (callId: string | null) => void;
  reset: () => void;
}

const initialState: AppState = {
  user: null,
  isConnected: false,
  isRegistered: false,
  activeCalls: new Map(),
  sipSessions: new Map(),
  callHistory: [],
  selectedCallId: null,
};

export const useCallStore = create<AppState & AppActions>((set, get) => ({
  ...initialState,

  setUser: (user) => set({ user }),

  setConnected: (connected) => set({ isConnected: connected }),

  setRegistered: (registered) => set({ isRegistered: registered }),

  addCall: (call) =>
    set((state) => {
      const newCalls = new Map(state.activeCalls);
      newCalls.set(call.call_uuid, call);
      return { activeCalls: newCalls };
    }),

  updateCall: (callUUID, updates) =>
    set((state) => {
      const newCalls = new Map(state.activeCalls);
      const existing = newCalls.get(callUUID);
      if (existing) {
        newCalls.set(callUUID, { ...existing, ...updates });
      }
      return { activeCalls: newCalls };
    }),

  removeCall: (callUUID, finalCallData?: Call) =>
    set((state) => {
      const newCalls = new Map(state.activeCalls);
      const existingCall = newCalls.get(callUUID);
      if (existingCall || finalCallData) {
        newCalls.delete(callUUID);
        // Use the final call data from backend if provided, merged with existing data
        const callForHistory = finalCallData
          ? { ...existingCall, ...finalCallData }
          : existingCall;
        if (callForHistory) {
          get().addToCallHistory(callForHistory);
        }
      }
      return { activeCalls: newCalls };
    }),

  addSIPSession: (session) =>
    set((state) => {
      const newSessions = new Map(state.sipSessions);
      newSessions.set(session.id, session);
      return { sipSessions: newSessions };
    }),

  updateSIPSession: (sessionId, updates) =>
    set((state) => {
      const newSessions = new Map(state.sipSessions);
      const existing = newSessions.get(sessionId);
      if (existing) {
        newSessions.set(sessionId, { ...existing, ...updates });
      }
      return { sipSessions: newSessions };
    }),

  removeSIPSession: (sessionId) =>
    set((state) => {
      const newSessions = new Map(state.sipSessions);
      newSessions.delete(sessionId);
      return { sipSessions: newSessions };
    }),

  setCallHistory: (history) => set({ callHistory: history }),

  addToCallHistory: (call) =>
    set((state) => {
      // Check if this call already exists in history (by call_uuid)
      const existingIndex = state.callHistory.findIndex(
        (c) => c.call_uuid === call.call_uuid
      );
      if (existingIndex >= 0) {
        // Update existing call in place with new data
        const updatedHistory = [...state.callHistory];
        updatedHistory[existingIndex] = { ...updatedHistory[existingIndex], ...call };
        return { callHistory: updatedHistory };
      }
      // Add new call to the beginning
      return { callHistory: [call, ...state.callHistory].slice(0, 100) };
    }),

  setSelectedCallId: (callId) => set({ selectedCallId: callId }),

  reset: () => set(initialState),
}));
